import { isNotificationCenterOnly } from "@/features/notifications/lib/notificationDedicatedPages";
import type { AppNotification } from "@/features/notifications/types";

export type NotificationNavigationTarget = {
  pathname: string;
  search?: string;
};

const NOTIFICATIONS_PAGE_TARGET: NotificationNavigationTarget = {
  pathname: "/notifications",
};

function buildSearch(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `?${query}` : "";
}

function metaId(
  notification: AppNotification,
  ...keys: string[]
): number | undefined {
  const metadata = notification.metadata ?? {};
  for (const key of keys) {
    const raw = metadata[key];
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === "string" && raw.trim()) {
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return notification.entity_id ?? undefined;
}

function audience(notification: AppNotification): string {
  const value = notification.metadata?.audience;
  return typeof value === "string" ? value : "user";
}

export function resolveNotificationTarget(
  notification: AppNotification,
): NotificationNavigationTarget {
  if (isNotificationCenterOnly(notification)) {
    return NOTIFICATIONS_PAGE_TARGET;
  }

  const eventType = notification.event_type;
  const entityType = notification.entity_type;
  const aud = audience(notification);

  switch (eventType) {
    case "salle.reservationCreated":
    case "salle.reservationUpdated":
    case "salle.reservationDeleted":
      return {
        pathname: "/meetings",
        search: buildSearch({
          panel: "my",
          id: metaId(notification, "reservationId"),
        }),
      };

    case "services.reservationCreated":
    case "services.reservationUpdated":
    case "services.reservationCancelled":
    case "services.reservationReminder":
      return {
        pathname: "/services/reservations",
        search: buildSearch({ id: metaId(notification, "reservationId") }),
      };

    case "services.slotChanged":
      return { pathname: "/services/reservations" };

    case "list.appointmentArrived":
    case "list.appointmentDeleted":
    case "list.listUpdated":
      return {
        pathname: "/appointments",
        search: buildSearch({
          tab: "all",
          id: metaId(notification, "appointmentId"),
        }),
      };

    case "cart.orderCreated":
      return { pathname: "/concierge/history" };

    case "cart.orderStatusChanged":
      return {
        pathname: "/concierge/history",
        search: buildSearch({ id: metaId(notification, "orderId") }),
      };

    case "cartBar.orderCreated":
      return { pathname: "/bar/history" };

    case "cartBar.orderStatusChanged":
      return {
        pathname: "/bar/history",
        search: buildSearch({ id: metaId(notification, "orderId") }),
      };

    case "ticketing.ticketAssigned":
      return {
        pathname: "/ticketing/assignments",
        search: buildSearch({ id: metaId(notification, "ticketId") }),
      };

    case "ticketing.ticketCreated":
    case "ticketing.ticketUpdated":
    case "ticketing.ticketMerged":
    case "ticketing.ticketResolved":
    case "ticketing.ticketDeleted":
      if (aud === "staff") {
        return {
          pathname: "/ticketing/all",
          search: buildSearch({ id: metaId(notification, "ticketId") }),
        };
      }
      return {
        pathname: "/ticketing/report",
        search: buildSearch({ id: metaId(notification, "ticketId") }),
      };

    case "messaging.messageReceive":
      return {
        pathname: "/messagerie",
        search: buildSearch({
          conversation: metaId(notification, "conversationId"),
        }),
      };

    default:
      break;
  }

  switch (entityType) {
    case "room_reservation":
      return { pathname: "/meetings", search: buildSearch({ panel: "my" }) };
    case "service_reservation":
      return { pathname: "/services/reservations" };
    case "appointment":
      return { pathname: "/appointments", search: buildSearch({ tab: "all" }) };
    case "cart_order":
      return { pathname: "/concierge/history" };
    case "cart_bar_order":
      return { pathname: "/bar/history" };
    case "ticket":
      return {
        pathname: aud === "staff" ? "/ticketing/all" : "/ticketing/report",
      };
    case "message":
      return { pathname: "/messagerie" };
    default:
      return NOTIFICATIONS_PAGE_TARGET;
  }
}
